/** Primitivas de dibujo compartidas por los bloques de la lámina. */

/** Escribe texto con salto de línea automático. Devuelve la Y siguiente. */
export function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  let cursorY = y;
  words.forEach((word) => {
    const candidate = `${line + word} `;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      ctx.fillText(line.trim(), x, cursorY);
      line = `${word} `;
      cursorY += lineHeight;
    } else {
      line = candidate;
    }
  });
  if (line) {
    ctx.fillText(line.trim(), x, cursorY);
    cursorY += lineHeight;
  }
  return cursorY;
}

/**
 * Cuántas líneas ocupará un texto. Se usa para dimensionar un recuadro ANTES de
 * dibujarlo, así la caja se ajusta al contenido en vez de recortarlo.
 * Usa su propio contexto para no ensuciar el estado del canvas real.
 */
const measureCtx =
  typeof document !== 'undefined' ? document.createElement('canvas').getContext('2d') : null;

export function countLines(text, font, maxWidth) {
  if (!measureCtx) return 1;
  measureCtx.font = font;
  const words = text.split(' ');
  let line = '';
  let lines = 1;
  words.forEach((word) => {
    const candidate = `${line + word} `;
    if (measureCtx.measureText(candidate).width > maxWidth && line) {
      lines++;
      line = `${word} `;
    } else {
      line = candidate;
    }
  });
  return lines;
}

/** Recorta un texto con elipsis para que no exceda el ancho disponible. */
export function clipText(ctx, text, maxWidth) {
  if (maxWidth <= 0 || ctx.measureText(text).width <= maxWidth) return text;
  let clipped = text;
  while (clipped.length && ctx.measureText(`${clipped}…`).width > maxWidth) {
    clipped = clipped.slice(0, -1);
  }
  return `${clipped}…`;
}

/** `#rrggbb` + alfa → `rgba(...)`. */
export function hexToRgba(hex, alpha) {
  const value = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${value & 255},${alpha})`;
}

/**
 * Veredicto en forma de pastilla sólida con ícono vectorial de check o cruz.
 * El ícono se dibuja en vectores en vez de usar un carácter tipográfico porque
 * el emoji no rasteriza igual en todas las máquinas y la lámina es un entregable.
 *
 * @param {number} anchorX Borde derecho de la pastilla, o su centro si `centered`.
 * @returns {number} La X izquierda resultante, para acotar el texto que va al lado.
 */
export function drawVerdictPill(ctx, anchorX, midY, ok, text, fontSize = 12, centered = false) {
  ctx.font = `bold ${fontSize}px Arial`;
  const textWidth = ctx.measureText(text).width;
  const iconWidth = Math.round(fontSize * 0.9);
  const padLeft = 9;
  const gap = 6;
  const padRight = 12;
  const width = padLeft + iconWidth + gap + textWidth + padRight;
  const height = fontSize + 12;
  const x = centered ? anchorX - width / 2 : anchorX - width;
  const y = midY - height / 2;

  ctx.fillStyle = ok ? '#2f9e4f' : '#d13a5e';
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, height / 2);
  ctx.fill();

  const iconCx = x + padLeft + iconWidth / 2;
  const radius = iconWidth / 2;
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.max(2, fontSize * 0.17);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  if (ok) {
    ctx.moveTo(iconCx - radius * 0.55, midY + radius * 0.05);
    ctx.lineTo(iconCx - radius * 0.1, midY + radius * 0.5);
    ctx.lineTo(iconCx + radius * 0.6, midY - radius * 0.55);
  } else {
    ctx.moveTo(iconCx - radius * 0.5, midY - radius * 0.5);
    ctx.lineTo(iconCx + radius * 0.5, midY + radius * 0.5);
    ctx.moveTo(iconCx - radius * 0.5, midY + radius * 0.5);
    ctx.lineTo(iconCx + radius * 0.5, midY - radius * 0.5);
  }
  ctx.stroke();

  ctx.fillStyle = '#fff';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.fillText(text, x + padLeft + iconWidth + gap, midY);
  return x;
}

/** Caja redondeada con relleno y borde, el envase estándar de los bloques. */
export function drawPanel(ctx, x, y, width, height, { fill, stroke, lineWidth = 1.5, radius = 10 }) {
  ctx.fillStyle = fill;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.roundRect(x, y, width, height, radius);
  ctx.fill();
  ctx.stroke();
}

/**
 * Rosa de los vientos de 8 puntas, orientada al norte REAL de la imagen.
 * `angle` es el ángulo en pantalla hacia donde apunta el norte, en radianes:
 * con el mapa rotado, el norte de la lámina no es "arriba".
 */
export function drawCompass(ctx, cx, cy, radius, angle) {
  const normalize = (x, y) => {
    const length = Math.hypot(x, y) || 1;
    return [x / length, y / length];
  };
  const nx = Math.cos(angle);
  const ny = Math.sin(angle);
  const N = [nx, ny];
  const S = [-nx, -ny];
  const E = [-ny, nx];
  const W = [ny, -nx];
  const NE = normalize(N[0] + E[0], N[1] + E[1]);
  const SE = normalize(S[0] + E[0], S[1] + E[1]);
  const SW = normalize(S[0] + W[0], S[1] + W[1]);
  const NW = normalize(N[0] + W[0], N[1] + W[1]);

  const spike = (dir, length, halfWidth, colorLeft, colorRight) => {
    const [dx, dy] = dir;
    const px = -dy;
    const py = dx;
    const tipX = cx + dx * length;
    const tipY = cy + dy * length;
    ctx.fillStyle = colorRight;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(cx + px * halfWidth, cy + py * halfWidth);
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = colorLeft;
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(cx - px * halfWidth, cy - py * halfWidth);
    ctx.lineTo(cx, cy);
    ctx.closePath();
    ctx.fill();
  };

  [NE, SE, SW, NW].forEach((dir) => spike(dir, radius * 0.52, radius * 0.1, '#5f7488', '#93a7b8'));
  const mainWidth = radius * 0.15;
  spike(E, radius, mainWidth, '#7f94a6', '#c3d0da');
  spike(W, radius, mainWidth, '#7f94a6', '#c3d0da');
  spike(S, radius, mainWidth, '#7f94a6', '#c3d0da');
  spike(N, radius * 1.06, mainWidth * 1.15, '#b5302e', '#e24b4a');

  ctx.beginPath();
  ctx.arc(cx, cy, 2.5, 0, 2 * Math.PI);
  ctx.fillStyle = '#14202b';
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = '#fff';
  ctx.stroke();

  const letter = (dir, text, size, color) => {
    const lx = cx + dir[0] * (radius + 9);
    const ly = cy + dir[1] * (radius + 9);
    ctx.font = `bold ${size}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(16,26,36,.9)';
    ctx.strokeText(text, lx, ly);
    ctx.fillStyle = color;
    ctx.fillText(text, lx, ly);
  };
  letter(N, 'N', 16, '#ff5b59');
  letter(S, 'S', 9, '#b9c6d2');
  letter(E, 'E', 9, '#b9c6d2');
  letter(W, 'W', 9, '#b9c6d2');
}

/** Cenefa que encuadra la lámina: filete oscuro grueso y filete blanco fino. */
export function drawFrame(ctx, width, height) {
  ctx.lineJoin = 'miter';
  ctx.strokeStyle = '#0a121a';
  ctx.lineWidth = 12;
  ctx.strokeRect(6, 6, width - 12, height - 12);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 3;
  ctx.strokeRect(14, 14, width - 28, height - 28);
}
